import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Event,
  GuardsCheckStart,
  NavigationEnd,
  Params,
  Router,
  RoutesRecognized
} from '@angular/router';
import {filter} from 'rxjs/operators';
import {BehaviorSubject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RouteParamsService {

  public routesConfig: any = {};

  public paramsSnapshot: Params = {};
  public params$: BehaviorSubject<Params> = new BehaviorSubject(this.paramsSnapshot);

  public nextParamsSnapshot: Params = {};
  public nextParams$: BehaviorSubject<Params> = new BehaviorSubject(this.nextParamsSnapshot);

  private static paramsAreDifferent(currentParams: Params, nextParams: Params): boolean {

    const currentKeys = Object.keys(currentParams);
    const nextKeys = Object.keys(nextParams);
    if (currentKeys.length !== nextKeys.length) {
      return true;
    }

    for (let i = 0, length = currentKeys.length; i < length; i++) {
      const key = currentKeys[i];
      if (currentParams[key] !== nextParams[key]) {
        return true;
      }
    }

    return false;
  }

  constructor(router: Router) {

    this.collectRoutesData(router.config);
    router.events
      .pipe(
        filter((event: Event): boolean => event instanceof RoutesRecognized))
      .subscribe(() => {
        this.collectRoutesData(router.config);
      });

    // collects route params for "future route" (before route guards decides if it's ok or not to complete navigation)
    router.events
      .pipe(
        filter((event: Event): boolean => event instanceof GuardsCheckStart))
      .subscribe((ev: GuardsCheckStart) => {
        const snapshot = ev.state.root;
        const nextParams = this.collectParams(snapshot);
        if (RouteParamsService.paramsAreDifferent(this.nextParamsSnapshot, nextParams)) {
          this.nextParams$.next(this.nextParamsSnapshot = nextParams);
        }
      });

    // collects route params for "current route" (after route has successfully loaded)
    router.events
      .pipe(
        filter((event: Event): boolean => event instanceof NavigationEnd))
      .subscribe(() => {
        const snapshot = router.routerState.snapshot.root;
        const currentParams = this.collectParams(snapshot);
        if (RouteParamsService.paramsAreDifferent(this.paramsSnapshot, currentParams)) {
          this.params$.next(this.paramsSnapshot = currentParams);
        }
      });
  }


  private collectRoutesData(config: any, initialPath: string = ''): void {

    if (!config) {
      return;
    }

    config.forEach(x => {

      if (x.redirectTo) {
        return;
      }

      const separator =
        initialPath?.length && !initialPath?.endsWith('/') && // (initialPath) isn't empty and doesn't end with with '/'
        x.path?.length && !x.path?.startsWith('/') // (x.path) isn't empty and doesn't start with with '/'
          ? '/' : '';

      const fullPath = initialPath + separator + x.path;
      const newData = x.data || {};
      const prevData = this.routesConfig[fullPath]?.data || {};
      this.routesConfig[fullPath] = {
        data: Object.assign(prevData, newData),
        hasComponent: !!x.component
      };
      this.collectRoutesData(x.children, fullPath);
      this.collectRoutesData(x._loadedRoutes, fullPath);
    });
  }

  // CAUTION: All params are merged into a single object. This means that like-named
  // params in different tree nodes will collide and overwrite each other.
  private collectParams(root: ActivatedRouteSnapshot): Params {

    const params: Params = {};
    (function mergeParamsFromSnapshot(snapshot: ActivatedRouteSnapshot): void {
      Object.assign(params, snapshot.params);
      snapshot.children.forEach(mergeParamsFromSnapshot);
    })(root);

    return (params);
  }
}
